/**
 * Per-company digit-style selection for the staff + customer-facing portal.
 *
 * Owner decision (2026-10-10): a per-company setting,
 * `companies.settings.number_digits`, picks the digit style used everywhere
 * this app formats a number or a date for a human to read. The same screen
 * or PDF had Arabic-Indic digits (١٢٣) sitting next to Latin digits (123) —
 * `unified-portal/src/utils/fmtMoney.js` pinned en-US, but every other date
 * and money formatter went with the UI language, and the two scripts mixed.
 *
 * Rules:
 *   - Two values: `'latin'` (123, default for new AND existing companies) and
 *     `'arabic'` (١٢٣). Missing/unknown → 'latin' (no migration, no backfill).
 *   - English UI always uses Latin digits.
 *   - Gregorian calendar everywhere it is used today (`-u-ca-gregory`); no
 *     screen switches to Hijri.
 *   - The setting is read once per session by the company-loading code
 *     (auth/initAuth etc.) and stored in this module. Callers do NOT need
 *     to know which company they are reading for.
 *
 * ZATCA XML/QR, accounting exports and APIs are deliberately untouched:
 * those need machine format (ISO dates, Latin digits) whatever the setting,
 * and changing them is not this lane's scope. See the call-site audit in
 * LANE-REPORT.md.
 */
import i18next from 'i18next';

// The UI language is read straight off the i18next singleton. src/i18n.js
// configures THAT instance (`import i18n from 'i18next'` is the same object),
// so reading `.language` here follows the running UI without importing
// src/i18n.js — importing it from a helper would drag the whole translation
// table into every bundle and CRASH every suite that mocks react-i18next
// (i18n.js passes `initReactI18next` to i18next.use() at module load, which
// throws when the mock made it undefined; 54 suites do exactly that).

const LATIN = 'latin';
const ARABIC = 'arabic';

// Module-level mirror of companies.settings.number_digits. The portal loads
// the company settings once at boot (see callers of `setNumberDigits`); until
// then the default keeps formatting in Latin, matching the spec's
// "default Latin for new AND existing companies".
let currentNumberDigits = LATIN;

/**
 * Set the company's digit-style setting. Pass `null`/`undefined`/anything
 * not in {latin, arabic} to reset to Latin (matches the "missing/unknown →
 * latin" rule).
 *
 * @param {string|null|undefined} value
 */
export function setNumberDigits(value) {
  currentNumberDigits = value === ARABIC ? ARABIC : LATIN;
}

/**
 * Read the company's digit-style setting. Defaults to Latin.
 *
 * @returns {string} 'latin' | 'arabic'
 */
export function getNumberDigits() {
  return currentNumberDigits;
}

/**
 * The CURRENT UI language, reduced to the line-language vocabulary the
 * formatting helpers use: 'ar' for any Arabic tag (i18next.language can be
 * 'ar', 'ar-SA', …), 'en' otherwise — including before src/i18n.js has run
 * i18next.init(), where Latin keeps the pre-lane behaviour.
 *
 * This is what a formatting helper means by "the language of the line" when
 * the caller has none to give (round 5): an English screen prints Latin, an
 * Arabic screen follows the company digit setting.
 *
 * @returns {string} 'ar' | 'en'
 */
export function uiLanguage() {
  const lang = i18next && i18next.language;
  return typeof lang === 'string' && lang.indexOf('ar') === 0 ? 'ar' : 'en';
}

/**
 * Pick the right Intl locale for a language + the company's digit setting.
 * English is always Latin (the English UI never uses ١٢٣, by owner decision).
 * Arabic gets `nu-latn` (Latin digits) under the default, and `nu-arab`
 * (Arabic-Indic digits) under `'arabic'`. The Gregorian calendar stays put.
 *
 * The English locale is an argument: screens whose base formatting used
 * 'en-GB' (day/month/year) pass `{ en: 'en-GB' }` so the migration to this
 * helper did not silently reorder their English dates to en-US
 * month/day/year. Sites that always read en-US call it without the argument.
 *
 * @param {string} [lang]   i18n language, e.g. 'ar' or 'en'. Anything not 'ar'
 *                          reads as English.
 * @param {object} [opts]   `{ en: 'en-GB' }` — the English locale to return.
 * @returns {string}        An Intl locale string the formatter can consume.
 */
export function appLocale(lang, opts) {
  const isArabic = lang === 'ar';
  if (!isArabic) return (opts && opts.en) || 'en-US';
  return currentNumberDigits === ARABIC
    ? 'ar-SA-u-ca-gregory-nu-arab'
    : 'ar-SA-u-ca-gregory-nu-latn';
}

/**
 * Round-trip digit swap. Used by hand-built strings the Intl pipeline never
 * sees — e.g. a `toFixed(2)` result, a manually-composed money line, an SMS.
 * Latin ↔ Arabic-Indic. Other characters (Latin letters, Arabic text,
 * punctuation) pass through unchanged.
 *
 * @param {string|number|null|undefined} value
 * @param {string} [lang]   i18n language. 'ar' with the Arabic choice flips
 *                          digits to Arabic-Indic; anything else returns
 *                          Latin digits.
 * @returns {string|number|null|undefined}
 */
export function localizeDigits(value, lang) {
  if (value === null || value === undefined) return value;
  if (typeof value !== 'string') return value;

  const wantArabic = lang === 'ar' && currentNumberDigits === ARABIC;

  // ٠..٩ ↔ 0..9 (Arabic-Indic). Persian ۰..۹ also ↔ 0..9, so they go through
  // the same table when Latin is wanted.
  const ARABIC_ZERO = 0x0660;
  const LATIN_ZERO = 0x0030;
  const PERSIAN_ZERO = 0x06f0;

  if (!wantArabic) {
    return value.replace(/[٠-٩۰-۹]/g, (ch) => {
      const code = ch.charCodeAt(0);
      if (code >= ARABIC_ZERO && code <= ARABIC_ZERO + 9) {
        return String.fromCharCode(code - ARABIC_ZERO + LATIN_ZERO);
      }
      // Persian/Eastern
      return String.fromCharCode(code - PERSIAN_ZERO + LATIN_ZERO);
    });
  }

  return value.replace(/[0-9]/g, (ch) =>
    String.fromCharCode(ch.charCodeAt(0) - LATIN_ZERO + ARABIC_ZERO)
  );
}

/**
 * Hook: subscribe to the current setting. Returns the value and a setter —
 * use the setter from a once-only bootstrap (after the company payload
 * loads) so callers never have to thread the setting through their props.
 *
 * @returns {string} the current `'latin'` | `'arabic'`.
 */
export function useNumberDigits() {
  return currentNumberDigits;
}

export default {
  appLocale,
  localizeDigits,
  setNumberDigits,
  getNumberDigits,
  useNumberDigits,
  uiLanguage,
};
